import React, { useEffect, useState } from 'react';
import { CloseableModal } from '../../../../../../components/CloseableModal';
import { OfferRequisition } from '../../../../../../entities/v1/applicant_tracking/OfferRequisition';
import {
  buildSteps,
  getCurrentStepOrder,
} from '../../../../../../utils/applicant_tracking/requisition';
import { getOrdinal } from '../../../../../../utils/number';
import { Button } from 'reactstrap';
import { OfferRequisitionService } from '../../../../../../services/v1/applicant_tracking/OfferRequisitionService';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner';

interface PropTypes {
  offerRequisitionId: number;
  offerRequisition?: OfferRequisition;
  onClose: (succeeded?: boolean) => void;
}

const currentApproverNames = (
  offerRequisition: OfferRequisition,
  currentStepOrder: number,
) => {
  return offerRequisition.approvers
    .filter((approver) => approver.order === currentStepOrder)
    .map((approver, index) => (
      <span key={index} className='fw-semibold'>
        {approver.user.name}
      </span>
    ));
};

function ApproverNames(props: PropTypes & { currentStepOrder: number }) {
  return (
    <>
      {currentApproverNames(
        props.offerRequisition,
        props.currentStepOrder,
      ).reduce((prev, curr, index) => {
        return index === 0 ? [curr] : [...prev, ' or ', curr];
      }, [] as React.ReactNode[])}
    </>
  );
}

function ModalBody(props: PropTypes & { isLoading: boolean }) {
  if (props.isLoading) {
    return <LoadingSpinner />;
  }

  const currentStepOrder = getCurrentStepOrder(
    buildSteps(props.offerRequisition.approvers),
  );
  const numberOfCurrentApprovers = currentApproverNames(
    props.offerRequisition,
    currentStepOrder,
  ).length;

  const sendReminder = () => {
    OfferRequisitionService.sendReminder(props.offerRequisition.id)
      .then(() => props.onClose(true))
      .catch(() => props.onClose(false));
  };

  return (
    <div className='fs-5'>
      Your offer requisition is awaiting {getOrdinal(currentStepOrder)} approval
      from {numberOfCurrentApprovers > 1 && 'either '}
      <ApproverNames {...props} currentStepOrder={currentStepOrder} />.{' '}
      <br></br>
      <br></br>Are you sure you want to send a reminder to them?
      <div className='d-flex justify-content-end mt-4'>
        <Button
          color='secondary'
          className='me-3'
          onClick={() => props.onClose()}
        >
          Cancel
        </Button>
        <Button color='primary' onClick={sendReminder}>
          Send
        </Button>
      </div>
    </div>
  );
}

export function SendReminderModal(props: PropTypes) {
  const [offerRequisition, setOfferRequisition] =
    useState<OfferRequisition | null>(props.offerRequisition);
  const [isLoading, setIsLoading] = useState(!offerRequisition);

  useEffect(() => {
    if (offerRequisition) {
      return setIsLoading(false);
    }

    OfferRequisitionService.show({
      id: props.offerRequisitionId.toString(),
      expand: ['approvers', 'approvers.user'],
    })
      .then(setOfferRequisition)
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <CloseableModal
      className='modal-dialog-centered'
      onClose={() => props.onClose()}
      isOpen={true}
      size='md'
      closeDisabled={false}
      keyboard={true}
      backdrop={true}
      headerTitle='Send Reminder'
      bodyChildren={
        <ModalBody
          {...props}
          offerRequisition={offerRequisition}
          isLoading={isLoading}
        />
      }
    />
  );
}
